import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Checkbox,
    Box,
    Select,
    IconButton,
    Divider,
} from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import { useBIMStore } from '../../state/useBIMStore';
import { ManageDialogFrame, ManageDialogPrimaryButton } from '../manage/ManageDialogFrame';
import { notify } from '../../utils/notify';

/**
 * Copy/Monitor dialog (Collaborate > Coordinate > Copy/Monitor).
 * Choose whether coordinates are copied/monitored from a linked model
 * ("Select Link") or from the current project ("Use Current Project"),
 * then pick the datum categories to monitor.
 */
export function CopyMonitorDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'copyMonitor');
    const onClose = useAppStore((state) => state.closeCollabDialog);
    const preselectSource = useAppStore((state) => state.collabDialogPayload);
    const addMonitoredModel = useWorksharingStore((s) => s.addMonitoredModel);
    const monitoredModels = useWorksharingStore((s) => s.monitoredModels);
    const removeMonitoredModel = useWorksharingStore((s) => s.removeMonitoredModel);

    // Linked BIM models available as coordinate sources. Subscribed to the
    // element map (stable ref) and derived in a memo: the old inline selector
    // re-scanned every element with a fresh Set+array per store write.
    const elements = useBIMStore((s) => s.project.elements);
    const linkedFiles = useMemo(() => {
        const files = new Set<string>();
        elements.forEach((el) => {
            if (el.category === 'BIM Links') {
                const file = el.parameters?.['File Path'];
                if (typeof file === 'string' && file) files.add(file.split(/[\\/]/).pop() || file);
            }
        });
        return Array.from(files);
    }, [elements]);

    const defaultSource: 'current' | string =
        preselectSource === 'current' || linkedFiles.length === 0
            ? 'current'
            : linkedFiles[0];
    const [source, setSource] = useState<'current' | string>(defaultSource);
    const [levels, setLevels] = useState(true);
    const [grids, setGrids] = useState(true);
    const [columns, setColumns] = useState(false);

    const handleStart = () => {
        if (!levels && !grids && !columns) {
            notify('Select at least one category to copy/monitor.', 'warning');
            return;
        }
        const sourceName = source === 'current' ? 'Current Project' : source;
        addMonitoredModel(sourceName, { levels, grids, columns });
        onClose();
    };

    return (
        <ManageDialogFrame
            title={t('collab.copyMonitor')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <HStack spacing={2}>
                    <ManageDialogPrimaryButton
                        label={t('collab.startMonitoring')}
                        onClick={handleStart}
                        isDisabled={!levels && !grids && !columns}
                    />
                    <Button size="sm" variant="ghost" onClick={onClose}>{t('collab.cancel')}</Button>
                </HStack>
            }
        >
            <VStack spacing={4} align="stretch">
                <Box>
                    <Text fontSize="xs" fontWeight="semibold" mb={1}>{t('collab.coordinatesWillBeCopiedAndMonitoredFrom')}</Text>
                    <Select
                        size="sm"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        bg="var(--color-bg-primary)"
                        borderColor="var(--color-border)"
                        maxW="420px"
                    >
                        <option value="current">{t('collab.currentProjectUseCurrentProject')}</option>
                        {linkedFiles.map((f) => (
                            <option key={f} value={f}>{f} (Select Link)</option>
                        ))}
                    </Select>
                    {linkedFiles.length === 0 && (
                        <Text fontSize="xs" color="var(--color-text-secondary)" mt={1}>{t('collab.noLinkedBIMModelsFoundUseTheInsertTabLinkBIMToolToAttachAConsu')}</Text>
                    )}
                </Box>

                <Box>
                    <Text fontSize="xs" fontWeight="semibold" mb={1}>{t('collab.copyMonitorCategories')}</Text>
                    <HStack spacing={6}>
                        <Checkbox size="sm" isChecked={levels} onChange={(e) => setLevels(e.target.checked)}>
                            <Text fontSize="sm">{t('collab.levels')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={grids} onChange={(e) => setGrids(e.target.checked)}>
                            <Text fontSize="sm">{t('collab.grids')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={columns} onChange={(e) => setColumns(e.target.checked)}>
                            <Text fontSize="sm">{t('collab.structuralColumns')}</Text>
                        </Checkbox>
                    </HStack>
                    <Text fontSize="xs" color="var(--color-text-secondary)" mt={1}>{t('collab.whenAMonitoredElementChangesInTheSourceANotificationAppearsInCoordi')}</Text>
                </Box>

                {monitoredModels.length > 0 && (
                    <>
                        <Divider />
                        <Box>
                            <Text fontSize="xs" fontWeight="semibold" mb={1}>Currently monitored ({monitoredModels.length})</Text>
                            <VStack align="stretch" spacing={1}>
                                {monitoredModels.map((m) => (
                                    <HStack key={m.id} p={1.5} borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-sm)">
                                        <Box flex="1 1 auto" minW={0}>
                                            <Text fontSize="sm" isTruncated>{m.sourceName}</Text>
                                            <Text fontSize="xs" color="var(--color-text-secondary)">
                                                {[
                                                    m.monitorLevels && 'Levels',
                                                    m.monitorGrids && 'Grids',
                                                    m.monitorColumns && 'Columns',
                                                ].filter(Boolean).join(', ')}
                                            </Text>
                                        </Box>
                                        <IconButton
                                            aria-label={t('collab.stopMonitoring')}
                                            icon={<Trash2 size={13} />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => removeMonitoredModel(m.id)}
                                        />
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>
                    </>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
